import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useCart } from '../contexts/CartContext';
import { HighlightedText } from '../components/HighlightedText';
import { toast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAGE_SIZE = 20;

const Products = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Read initial state from URL so links / share are deep-linkable.
  const [searchTerm, setSearchTerm] = useState(searchParams.get('q') || searchParams.get('search') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'name');
  const [page, setPage] = useState(parseInt(searchParams.get('page') || '1', 10));

  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addToCart } = useCart();

  // Debounce search term (so we don't hit backend on every keystroke).
  const [debouncedQ, setDebouncedQ] = useState(searchTerm);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(searchTerm), 350);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Reset to page 1 whenever search or category CHANGES (not on mount).
  // We compare against the initial values so that a deep link like
  // /products?q=battery&page=3 survives the first render even under Strict Mode.
  const initialFiltersRef = React.useRef({ q: debouncedQ, cat: selectedCategory });
  useEffect(() => {
    const init = initialFiltersRef.current;
    if (init.q === debouncedQ && init.cat === selectedCategory) return;
    setPage(1);
  }, [debouncedQ, selectedCategory]);

  // Fetch categories once.
  useEffect(() => {
    axios.get(`${API}/categories`)
      .then((res) => setCategories(res.data || []))
      .catch(() => setCategories([]));
  }, []);

  // Fetch products whenever filters / page change.
  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        paginated: 1,
        page,
        page_size: PAGE_SIZE,
      };
      if (debouncedQ.trim()) params.q = debouncedQ.trim();
      if (selectedCategory && selectedCategory !== 'all') params.category = selectedCategory;
      const response = await axios.get(`${API}/products`, { params });
      const data = response.data || {};
      setProducts(data.items || []);
      setTotal(data.total || 0);
      setTotalPages(Math.max(1, data.pages || 1));
    } catch (error) {
      console.error('Error fetching products:', error);
      toast({ title: 'Error', description: 'Failed to load products', variant: 'destructive' });
      setProducts([]);
      setTotal(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [debouncedQ, selectedCategory, page]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  // Keep the URL in sync so users can share filtered/paged links.
  useEffect(() => {
    const next = new URLSearchParams();
    if (debouncedQ.trim()) next.set('q', debouncedQ.trim());
    if (selectedCategory && selectedCategory !== 'all') next.set('category', selectedCategory);
    if (sortBy && sortBy !== 'name') next.set('sort', sortBy);
    if (page > 1) next.set('page', String(page));
    setSearchParams(next, { replace: true });
  }, [debouncedQ, selectedCategory, sortBy, page, setSearchParams]);

  // Client-side sort (backend sorts by name; we allow price re-sort within the current page).
  const displayed = useMemo(() => {
    const list = [...products];
    if (sortBy === 'price-low') list.sort((a, b) => Number(a.price) - Number(b.price));
    else if (sortBy === 'price-high') list.sort((a, b) => Number(b.price) - Number(a.price));
    return list;
  }, [products, sortBy]);

  const handleAddToCart = (product, e) => {
    e.preventDefault();
    addToCart(product);
    toast({ title: 'Added to cart', description: `${product.name} has been added to your cart.` });
  };

  const firstShown = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const lastShown = Math.min(total, page * PAGE_SIZE);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-4xl font-bold mb-8 text-slate-800">All Products</h1>

        {/* Filters */}
        <div className="bg-white p-6 rounded-lg shadow-md mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 h-4 w-4" />
              <Input
                type="text"
                placeholder="Search by name, description or SKU…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="products-search-input"
              />
            </div>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger data-testid="products-category-select">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.id || cat.slug} value={cat.slug}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger data-testid="products-sort-select">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name">Name (A-Z)</SelectItem>
                <SelectItem value="price-low">Price (Low to High)</SelectItem>
                <SelectItem value="price-high">Price (High to Low)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Result meta */}
        <div className="flex items-center justify-between mb-4 text-sm text-slate-600">
          <div data-testid="products-result-meta">
            {loading ? 'Loading…' : total === 0 ? 'No results' : `Showing ${firstShown}–${lastShown} of ${total} product${total === 1 ? '' : 's'}`}
          </div>
          <div className="text-xs text-slate-400">Page {page} of {totalPages}</div>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-10 w-10 animate-spin text-blue-600" /></div>
        ) : displayed.length === 0 ? (
          <div className="text-center py-12" data-testid="products-empty">
            <p className="text-slate-500 text-lg">No products found matching your criteria.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {displayed.map((product, idx) => (
              <Link key={`${product.id}-${idx}`} to={`/product/${product.id}`} className="group" data-testid={`product-card-${product.id}`}>
                <Card className="h-full hover:shadow-xl transition-all duration-300 border-2 hover:border-blue-500">
                  <CardContent className="p-4">
                    <div className="aspect-square bg-slate-100 rounded-lg mb-4 overflow-hidden">
                      <img src={product.image} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    </div>
                    <h3 className="font-semibold text-slate-800 mb-2 line-clamp-2 group-hover:text-blue-600 transition-colors">
                      <HighlightedText text={product.name} query={searchTerm} />
                    </h3>
                    <p className="text-sm text-slate-500 mb-3 line-clamp-2">{product.description}</p>
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-xl font-bold text-blue-600">${(product.retail_price ?? product.price)}</span>
                      <span className={`text-sm ${product.stock > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {product.stock > 0 ? `${product.stock} in stock` : 'Out of Stock'}
                      </span>
                    </div>
                    <Button
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                      onClick={(e) => handleAddToCart(product, e)}
                      disabled={product.stock === 0}
                      data-testid={`add-to-cart-${product.id}`}
                    >
                      Add to Cart
                    </Button>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        )}
      </div>
    </div>
  );
};

const Pagination = ({ page, totalPages, onChange }) => {
  // Generate a compact pager: 1 … (p-1) (p) (p+1) … N
  const pages = useMemo(() => {
    const set = new Set([1, totalPages, page, page - 1, page + 1]);
    return [...set].filter((n) => n >= 1 && n <= totalPages).sort((a, b) => a - b);
  }, [page, totalPages]);

  return (
    <nav className="flex items-center justify-center gap-1 mt-8" data-testid="products-pagination">
      <Button variant="outline" size="sm" onClick={() => onChange(Math.max(1, page - 1))} disabled={page === 1} data-testid="pager-prev">
        <ChevronLeft className="h-4 w-4" /> Prev
      </Button>
      {pages.map((n, i) => {
        const prev = pages[i - 1];
        return (
          <React.Fragment key={n}>
            {prev !== undefined && n - prev > 1 && <span className="px-2 text-slate-400">…</span>}
            <Button
              variant={n === page ? 'default' : 'outline'}
              size="sm"
              className={n === page ? 'bg-blue-600 hover:bg-blue-700 text-white min-w-[36px]' : 'min-w-[36px]'}
              onClick={() => onChange(n)}
              data-testid={`pager-page-${n}`}
            >
              {n}
            </Button>
          </React.Fragment>
        );
      })}
      <Button variant="outline" size="sm" onClick={() => onChange(Math.min(totalPages, page + 1))} disabled={page === totalPages} data-testid="pager-next">
        Next <ChevronRight className="h-4 w-4" />
      </Button>
    </nav>
  );
};

export default Products;
